import {useEffect, useRef, useState} from "react";
import {Button, Input, Modal, QRCode} from "antd";
import {useSetRecoilState} from "recoil";
import {useTranslation} from "react-i18next";
import {globalNotificationState} from "./global_state";
import useTwoFactorGuard from "./useTwoFactorGuard";
import {backendMessage} from "./backendMessage";
import {confirmTotpEnrolmentMutation, startTotpEnrolmentMutation} from "../api/graphql/userTotp";

// the setup dialog also opens from the shared verification dialog, which knows nothing of the
// cards showing the app, so a finished setup is announced for every card to reread
export const TOTP_CHANGED_EVENT = "totp:changed";

const APP_STORE_URL = "https://apps.apple.com/app/google-authenticator/id388497605";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2";

// these keys are new, so the copy ships with them until the cms has its own
const TOTP_COPY = {
    "admin.err.two_factor_totp_setup_invalid_code":
        "That code didn't match. Check the time on your phone is set automatically, then enter the newest code.",
    "admin.err.two_factor_totp_setup_expired": "Setup timed out. Please start again.",
    "admin.err.two_factor_totp_setup_attempts_exhausted": "Too many codes didn't match. Please start again.",
    "admin.err.two_factor_totp_locked":
        "Your authenticator app is locked after too many incorrect codes. Set it up again to keep using it.",
    "admin.err.two_factor_totp_unavailable": "Your authenticator app is no longer set up.",
    "admin.err.two_factor_totp_code_used": "That code was already used. Please wait for the next one.",
    "admin.err.two_factor_totp_setup_required": "Set up an authenticator app to continue.",
};

export const totpMessage = (t, errKey) =>
    TOTP_COPY[errKey] ? t(errKey, {defaultValue: TOTP_COPY[errKey]}) : backendMessage(t, errKey);

// a setup that cannot be retried is closed rather than left offering a box that always fails
const SETUP_TERMINAL_KEYS = [
    "admin.err.two_factor_totp_setup_expired",
    "admin.err.two_factor_totp_setup_attempts_exhausted",
];

// a phone cannot scan its own screen, so there the app is opened by link and the qr waits
const isTouchDevice = () => !!window.matchMedia?.("(pointer: coarse)")?.matches;

const brandButton = {
    // the dialog is a portal, out of reach of the brand stylesheet
    backgroundColor: "var(--primary-btn-bg-color)",
    color: "var(--primary-btn-text-color)",
    borderColor: "var(--primary-btn-border-color)",
};

const linkStyle = {
    fontSize: 13,
    cursor: "pointer",
    color: "var(--link-color, var(--primary-btn-bg-color))",
    background: "none",
    border: "none",
    padding: 0,
    textAlign: "left",
};

const Step = ({number, title, children}) => (
    <div style={{display: "flex", gap: 12}}>
        <div
            style={{
                flexShrink: 0,
                width: 24,
                height: 24,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 600,
                background: "var(--primary-btn-bg-color)",
                color: "var(--primary-btn-text-color)",
            }}
        >
            {number}
        </div>
        <div style={{display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flexGrow: 1}}>
            <div style={{fontSize: 14, fontWeight: 500, color: "var(--primary-text-color)"}}>{title}</div>
            {children}
        </div>
    </div>
);

const TotpSetupModal = ({enrolment, onClose, onConfirmed, notify}) => {
    const {t} = useTranslation();
    const touch = isTouchDevice();

    const [code, setCode] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showKey, setShowKey] = useState(touch);
    const [showQr, setShowQr] = useState(!touch);
    const [focused, setFocused] = useState(false);
    const submitted = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        setCode("");
        setError(null);
        setBusy(false);
        setShowKey(touch);
        setShowQr(!touch);
        submitted.current = null;
    }, [enrolment, touch]);

    const copyKey = async () => {
        const raw = (enrolment?.secret || "").replace(/\s/g, "");

        try {
            await navigator.clipboard.writeText(raw);
        } catch (ignore) {
            const field = document.createElement("textarea");

            field.value = raw;
            document.body.appendChild(field);
            field.select();
            document.execCommand("copy");
            field.remove();
        }

        notify("success", t("twoFactor.totpKeyCopied", {defaultValue: "Setup key copied"}), "totpKeyCopied");
    };

    const confirm = async (value = code) => {
        if (busy || value.length < 6 || submitted.current === value) return;

        submitted.current = value;
        setBusy(true);
        setError(null);

        try {
            const response = await confirmTotpEnrolmentMutation(value);
            const payload = response?.confirmTotpEnrolment;

            if (payload?.__typename === "BaseError" || !payload) {
                if (SETUP_TERMINAL_KEYS.includes(payload?.errKey)) {
                    notify("error", totpMessage(t, payload?.errKey), "totpSetupEnded");
                    onClose();
                    return;
                }

                setError(totpMessage(t, payload?.errKey));
                setCode("");
                submitted.current = null;
                setTimeout(() => inputRef.current?.focus(), 100);
                return;
            }

            onConfirmed(payload);
        } catch (ignore) {
            submitted.current = null;
            setError(backendMessage(t));
        } finally {
            setBusy(false);
        }
    };

    const onChange = (event) => {
        const value = event.target.value.replace(/\D/g, "").slice(0, 6);

        setCode(value);
        if (error) setError(null);
        if (value.length === 6) confirm(value);
    };

    const qr = (
        // white plate: a qr has to stay light to scan on the dark brands
        <div style={{background: "#fff", padding: 10, borderRadius: 8, alignSelf: "flex-start"}}>
            <QRCode value={enrolment?.otpauthUrl || "-"} size={136} bordered={false} color={"#000000"} bgColor={"#ffffff"} />
        </div>
    );

    const key = (
        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid var(--divider-color)",
                    background: "var(--form-input-bg-color)",
                }}
            >
                <code
                    style={{
                        fontFamily: "ui-monospace, Consolas, monospace",
                        fontSize: 13,
                        letterSpacing: 1,
                        color: "var(--primary-text-color)",
                        overflowWrap: "anywhere",
                    }}
                >
                    {enrolment?.secret}
                </code>
                <Button
                    size={"small"}
                    onClick={copyKey}
                    // antd's default white reads as foreign on the dark brands
                    style={{
                        flexShrink: 0,
                        backgroundColor: "transparent",
                        border: "1px solid var(--primary-btn-bg-color)",
                        color: "var(--primary-btn-bg-color)",
                    }}
                >
                    {t("twoFactor.copy", {defaultValue: "Copy"})}
                </Button>
            </div>
            <div style={{fontSize: 12, color: "var(--primary-text-color)", opacity: 0.7, lineHeight: 1.5}}>
                {t("twoFactor.totpKeyHint", {
                    account: enrolment?.accountName,
                    defaultValue: "Account: {{account}}. Choose \"time-based\" if the app asks.",
                })}
            </div>
        </div>
    );

    return (
        <Modal
            open={!!enrolment}
            // tall enough to run off a laptop screen when pinned near the top
            centered
            zIndex={1310}
            title={t("twoFactor.totpSetupTitle", {defaultValue: "Set up authenticator app"})}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            maskClosable={false}
            width={440}
        >
            <div style={{display: "flex", flexDirection: "column", gap: 14, paddingTop: 4}}>
                <Step number={1} title={t("twoFactor.totpStepInstall", {defaultValue: "Get an authenticator app"})}>
                    <div style={{fontSize: 13, color: "var(--primary-text-color)", opacity: 0.85, lineHeight: 1.5}}>
                        {t("twoFactor.totpStepInstallHint", {
                            defaultValue: "Google Authenticator, Microsoft Authenticator or any similar app works.",
                        })}
                    </div>
                    <div style={{display: "flex", gap: 16, fontSize: 13}}>
                        <a href={APP_STORE_URL} target={"_blank"} rel={"noopener noreferrer"} style={linkStyle}>
                            App Store
                        </a>
                        <a href={PLAY_STORE_URL} target={"_blank"} rel={"noopener noreferrer"} style={linkStyle}>
                            Google Play
                        </a>
                    </div>
                </Step>

                <Step
                    number={2}
                    title={
                        touch
                            ? t("twoFactor.totpStepAddPhone", {defaultValue: "Add your account to the app"})
                            : t("twoFactor.totpStepScan", {defaultValue: "Scan this QR code with the app"})
                    }
                >
                    {touch ? (
                        <>
                            <Button type={"primary"} href={enrolment?.otpauthUrl} style={brandButton}>
                                {t("twoFactor.totpOpenApp", {defaultValue: "Open authenticator app"})}
                            </Button>
                            <div style={{fontSize: 13, color: "var(--primary-text-color)", opacity: 0.85}}>
                                {t("twoFactor.totpOrEnterKey", {defaultValue: "Or enter this setup key in the app:"})}
                            </div>
                            {key}
                            {showQr ? (
                                qr
                            ) : (
                                <button type={"button"} style={linkStyle} onClick={() => setShowQr(true)}>
                                    {t("twoFactor.totpShowQr", {defaultValue: "Show QR code to scan from another device"})}
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            {qr}
                            {showKey ? (
                                key
                            ) : (
                                <button type={"button"} style={linkStyle} onClick={() => setShowKey(true)}>
                                    {t("twoFactor.totpCantScan", {defaultValue: "Can't scan it? Enter a setup key instead"})}
                                </button>
                            )}
                        </>
                    )}
                </Step>

                <Step number={3} title={t("twoFactor.totpStepCode", {defaultValue: "Enter the 6-digit code from the app"})}>
                    <Input
                        ref={inputRef}
                        maxLength={6}
                        inputMode={"numeric"}
                        autoComplete={"one-time-code"}
                        value={code}
                        disabled={busy}
                        placeholder={"000000"}
                        status={error ? "error" : undefined}
                        aria-invalid={!!error}
                        aria-describedby={"totpSetupError"}
                        style={{
                            letterSpacing: 8,
                            paddingLeft: 8,
                            textAlign: "center",
                            fontSize: 16,
                            background: "var(--form-input-bg-color)",
                            borderColor: error
                                ? "#ff4d4f"
                                : focused
                                  ? "var(--primary-btn-bg-color)"
                                  : "var(--form-input-border-color)",
                            boxShadow: "none",
                        }}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        onChange={onChange}
                        onPressEnter={() => confirm()}
                    />
                    <div id={"totpSetupError"} role={"alert"} style={{fontSize: 12, color: "#ff4d4f", lineHeight: 1.5, minHeight: error ? undefined : 0}}>
                        {error}
                    </div>
                </Step>

                <Button
                    type={"primary"}
                    block
                    loading={busy}
                    disabled={busy || code.length < 6}
                    onClick={() => confirm()}
                    style={{...brandButton, opacity: code.length < 6 ? 0.5 : 1}}
                >
                    {t("twoFactor.totpTurnOn", {defaultValue: "Turn on"})}
                </Button>
            </div>
        </Modal>
    );
};

// setting up shows a secret, so it is guarded like any other authenticator change: the guard
// asks for the email code first, then the setup dialog opens on the secret the server returns
const useTotpEnrolment = ({onEnrolled, successMessage} = {}) => {
    const {t} = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState);
    const guard = useTwoFactorGuard();

    const [busy, setBusy] = useState(false);
    const [enrolment, setEnrolment] = useState(null);

    const notify = (status, msg, key) => setNotificationMessage({status, title: "", msg, key});

    const start = async () => {
        if (busy) return false;

        setBusy(true);

        try {
            const started = await guard.run({
                action: "totpEnrolment",
                attempt: async (challengeId) => {
                    const response = await startTotpEnrolmentMutation(challengeId);
                    return response?.startTotpEnrolment;
                },
            });

            if (!started) return false;

            if (started.__typename === "BaseError" || !started.otpauthUrl) {
                notify("error", totpMessage(t, started?.errKey), "totpSetupStartError");
                return false;
            }

            setEnrolment(started);
            return true;
        } catch (ignore) {
            notify("error", backendMessage(t), "totpSetupStartException");
            return false;
        } finally {
            setBusy(false);
        }
    };

    const modal = (
        <TotpSetupModal
            enrolment={enrolment}
            notify={notify}
            onClose={() => setEnrolment(null)}
            onConfirmed={(record) => {
                setEnrolment(null);
                window.dispatchEvent(new Event(TOTP_CHANGED_EVENT));
                notify(
                    "success",
                    successMessage || t("twoFactor.totpEnrolled", {defaultValue: "Authenticator app is set up"}),
                    "totpEnrolled",
                );
                onEnrolled?.(record);
            }}
        />
    );

    return {start, busy, modal};
};

export default useTotpEnrolment;
